<template>
  <Drawer
    :open="open"
    :title="t('vehicle.title')"
    :subtitle="stepSubtitle"
    :show-back="canGoBack"
    @close="$emit('close')"
    @back="goBack"
  >
    <!-- ── Banner récap (visible aux étapes model/luggage) ──────── -->
    <div v-if="showSummaryBanner" class="fg-veh-summary">
      <img
        v-if="selectedBrandLogo"
        :src="selectedBrandLogo"
        :alt="draft.brand_name"
        class="fg-veh-summary__logo"
      />
      <div class="fg-veh-summary__main">
        <div class="fg-veh-summary__tags">
          <span v-if="draft.brand_name" class="fg-veh-summary__tag">{{ draft.brand_name }}</span>
          <span v-if="draft.model_name" class="fg-veh-summary__tag">{{ draft.model_name }}</span>
        </div>
        <div v-if="hasDims" class="fg-veh-summary__dims">
          <span v-if="modelLengthM">{{ t('vehicle.summary.length') }} <strong>{{ modelLengthM.toFixed(2) }}m</strong></span>
          <span v-if="modelHeightM">{{ t('vehicle.summary.height') }} <strong>{{ modelHeightM.toFixed(2) }}m</strong></span>
        </div>
      </div>
    </div>

    <!-- ── Étape : Type ── -->
    <div v-if="step === 'type'" class="fg-veh__types">
      <button
        v-for="opt in TYPE_OPTIONS"
        :key="opt.value"
        type="button"
        class="fg-veh__type"
        :class="{ 'is-selected': draft.type === opt.value }"
        @click="pickType(opt.value)"
      >
        <span class="fg-veh__type-icon"><Icon :name="opt.icon" :size="22" /></span>
        <span class="fg-veh__type-label">{{ t(opt.labelKey) }}</span>
      </button>
    </div>

    <!-- ── Étape : Plaque (Moto) ── -->
    <div v-else-if="step === 'plate'" class="fg-form">
      <label class="fg-field">
        <span class="fg-field__label">{{ t('vehicle.plate') }}</span>
        <input v-model="draft.plate" type="text" placeholder="AB-123-CD" />
      </label>
    </div>

    <!-- ── Étape : Marque ── -->
    <div v-else-if="step === 'brand'">
      <input v-model="brandQuery" type="text" class="fg-search-input" :placeholder="t('vehicle.searchBrand')" />
      <Spinner v-if="brandsLoading" :label="t('common.loading')" />
      <ul v-else class="fg-veh__list">
        <li
          v-for="b in filteredBrands"
          :key="b.id"
          class="fg-row"
          :class="{ 'is-selected': draft.brand_id === b.id }"
          @click="pickBrand(b)"
        >
          <span v-if="b.logo" class="fg-veh__brand-logo"><img :src="normalizeBrandLogo(b.logo)" :alt="b.name" /></span>
          <span class="fg-veh__brand-name">{{ b.name }}</span>
        </li>
      </ul>
    </div>

    <!-- ── Étape : Modèle ── -->
    <div v-else-if="step === 'model'">
      <input v-model="modelQuery" type="text" class="fg-search-input" :placeholder="t('vehicle.searchModel')" />
      <Spinner v-if="modelsLoading" :label="t('common.loading')" />
      <ul v-else class="fg-veh__list">
        <li
          v-for="{ model: m, alias } in filteredModels"
          :key="m.id"
          class="fg-row"
          :class="{ 'is-selected': draft.model_id === m.id }"
          @click="pickModel(m)"
        >
          <span class="fg-veh__model-name">{{ m.model }}<span v-if="alias" class="fg-veh__model-alias"> ({{ alias }})</span></span>
          <span v-if="m.length" class="fg-veh__model-meta">{{ formatLengthMeters(m.length) }}m</span>
        </li>
      </ul>
    </div>

    <!-- ── Étape : Galerie toit + Caravane/Remorque ── -->
    <div v-else-if="step === 'luggage'" class="fg-veh-luggage">
      <!-- Galerie de toit -->
      <section class="fg-veh-luggage__section">
        <div class="fg-veh-luggage__radios">
          <button
            type="button"
            class="fg-veh-luggage__radio"
            :class="{ 'is-active': !draft.hasLuggage }"
            @click="draft.hasLuggage = false"
          >
            <span>{{ t('vehicle.roof.none') }}</span>
            <span class="fg-veh-luggage__dot" />
          </button>
          <button
            type="button"
            class="fg-veh-luggage__radio"
            :class="{ 'is-active': draft.hasLuggage }"
            @click="draft.hasLuggage = true"
          >
            <span>{{ t('vehicle.roof.with') }}</span>
            <span class="fg-veh-luggage__dot" />
          </button>
        </div>

        <div v-if="draft.hasLuggage" class="fg-veh-luggage__group">
          <div class="fg-veh-luggage__group-label">{{ t('vehicle.roof.heightExtra') }}</div>
          <div class="fg-veh-luggage__grid">
            <button
              v-for="h in ROOF_HEIGHTS"
              :key="h"
              type="button"
              class="fg-veh-luggage__chip"
              :class="{ 'is-active': draft.roofHeight === h }"
              @click="draft.roofHeight = h"
            >
              <span>{{ h }} cm</span>
              <span class="fg-veh-luggage__dot" />
            </button>
          </div>
        </div>
      </section>

      <!-- Caravane / Remorque -->
      <section class="fg-veh-luggage__section">
        <div class="fg-veh-luggage__section-title">{{ t('vehicle.trailer.title') }}</div>
        <div class="fg-veh-luggage__radios">
          <button
            type="button"
            class="fg-veh-luggage__radio"
            :class="{ 'is-active': !draft.hasTrailer }"
            @click="draft.hasTrailer = false"
          >
            <span>{{ t('vehicle.trailer.none') }}</span>
            <span class="fg-veh-luggage__dot" />
          </button>
          <button
            type="button"
            class="fg-veh-luggage__radio"
            :class="{ 'is-active': draft.hasTrailer }"
            @click="draft.hasTrailer = true"
          >
            <span>{{ t('vehicle.trailer.with') }}</span>
            <span class="fg-veh-luggage__dot" />
          </button>
        </div>

        <div v-if="draft.hasTrailer" class="fg-veh-luggage__group">
          <div class="fg-veh-luggage__toggle">
            <button
              type="button"
              class="fg-veh-luggage__toggle-btn"
              :class="{ 'is-active': draft.trailerKind === 'caravan' }"
              @click="draft.trailerKind = 'caravan'"
            >{{ t('vehicle.trailer.caravan') }}</button>
            <button
              type="button"
              class="fg-veh-luggage__toggle-btn"
              :class="{ 'is-active': draft.trailerKind === 'standard' }"
              @click="draft.trailerKind = 'standard'"
            >{{ t('vehicle.trailer.standard') }}</button>
          </div>

          <div class="fg-veh-luggage__group-label">{{ t('vehicle.trailer.lengthExtra') }}</div>
          <div class="fg-veh-luggage__grid">
            <button
              v-for="m in TRAILER_LENGTHS"
              :key="m"
              type="button"
              class="fg-veh-luggage__chip"
              :class="{ 'is-active': draft.trailerLength === m }"
              @click="draft.trailerLength = m"
            >
              <span>{{ m }} m</span>
              <span class="fg-veh-luggage__dot" />
            </button>
          </div>
        </div>
      </section>

      <!-- Synthèse des dims max -->
      <p v-if="hasDims" class="fg-veh-luggage__note">
        <Icon name="info" :size="12" />
        {{ t('vehicle.maxDims', {
          length: maxLengthM.toFixed(2),
          trailer: maxTrailerCmExtra,
          height: maxHeightM.toFixed(2),
          roof: maxRoofCmExtra,
        }) }}
      </p>
    </div>

    <template #footer>
      <button class="fg-btn fg-btn--primary fg-btn--block" type="button" :disabled="!canContinue" @click="goNext">
        {{ isFinalStep ? t('common.confirm') : t('common.continue') }}
      </button>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useStore } from '../../store'
import { useApi } from '../../api'
import { useI18n } from '../../i18n'
import type { Brand, Vehicle, VehicleModel } from '../../../../shared/types'
import { normalizeBrandLogo } from '../../utils/format'
import { matchVehicleModel, normalizeSearchText } from '../../../../shared/rules'
import Drawer from '../Drawer.vue'
import Icon from '../Icon.vue'
import Spinner from '../atoms/Spinner.vue'

type Step = 'type' | 'brand' | 'model' | 'luggage' | 'plate'
type VehicleType = 'NoCar' | 'Bike' | 'Car' | 'Van' | 'Moto'
type VehicleIcon = 'walker' | 'bike' | 'car' | 'van' | 'moto'
type TrailerKind = 'caravan' | 'standard'

interface TypeOption { value: VehicleType; icon: VehicleIcon; labelKey: string }

// Ordre demandé : Voiture, Van, Moto, Vélo, Aucun
const TYPE_OPTIONS: readonly TypeOption[] = [
  { value: 'Car',   icon: 'car',    labelKey: 'vehicle.typeCar' },
  { value: 'Van',   icon: 'van',    labelKey: 'vehicle.typeVan' },
  { value: 'Moto',  icon: 'moto',   labelKey: 'vehicle.typeMoto' },
  { value: 'Bike',  icon: 'bike',   labelKey: 'vehicle.typeBike' },
  { value: 'NoCar', icon: 'walker', labelKey: 'vehicle.typeNone' },
]

const SKIPPED_TYPES: ReadonlySet<VehicleType> = new Set(['NoCar', 'Bike'])

// Steps caravane/remorque — paliers présentés en chips radio.
const ROOF_HEIGHTS: readonly number[] = [25, 50, 75, 100, 125, 150, 175]
const TRAILER_LENGTHS: readonly number[] = [2, 3, 4, 5, 6, 7, 8, 9]

interface VehicleDraft {
  type: VehicleType | ''
  brand_id: string; brand_name: string
  model_id: string; model_name: string
  code: string; plate: string
  hasLuggage: boolean; roofHeight: number; roofLength: number
  hasTrailer: boolean; trailerKind: TrailerKind; trailerLength: number
}

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()

const store = useStore()
const api = useApi()
const { t } = useI18n()

const step = ref<Step>('type')
const draft = ref<VehicleDraft>(buildDraftFromStore())

const localBrands = ref<Brand[]>([])
const brandsLoading = ref(false)
const brandQuery = ref('')

const brands = computed<Brand[]>(() => store.brandCatalog.length > 0 ? store.brandCatalog : localBrands.value)

const models = ref<VehicleModel[]>([])
const modelsLoading = ref(false)
const modelQuery = ref('')

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    draft.value = buildDraftFromStore()
    step.value = 'type'
    brandQuery.value = ''
    modelQuery.value = ''
  }
})

const stepSubtitle = computed(() => {
  const order = stepOrder()
  const idx = order.indexOf(step.value)
  return idx >= 0 ? `${idx + 1}/${order.length} · ${t(`vehicle.step.${step.value}`)}` : ''
})

const canGoBack = computed(() => stepOrder().indexOf(step.value) > 0)
const isFinalStep = computed(() => {
  const order = stepOrder()
  return step.value === order[order.length - 1]
})

const canContinue = computed(() => {
  switch (step.value) {
    case 'type': return draft.value.type !== ''
    case 'plate': return draft.value.plate.trim().length > 0
    case 'brand': return draft.value.brand_id !== ''
    case 'model': return draft.value.model_id !== ''
    case 'luggage':
      // Pas de bagage / pas de remorque = OK. Si bagage : roofHeight requis.
      // Si remorque : trailerLength requis.
      if (draft.value.hasLuggage && !(draft.value.roofHeight > 0)) return false
      if (draft.value.hasTrailer && !(draft.value.trailerLength > 0)) return false
      return true
    default: return false
  }
})

const filteredBrands = computed(() => filterByText(brands.value, brandQuery.value, (b) => b.name))

// Modèles par ordre alphabétique, filtrés sur le nom ou un alias (SQ5 → Q5) ;
// l'alias trouvé est affiché à côté du nom.
const filteredModels = computed(() => models.value
  .map((model) => ({ model, ...matchVehicleModel(model, modelQuery.value) }))
  .filter((row) => row.matched)
  .sort((a, b) => a.model.model.localeCompare(b.model.model)))

// ─── Banner récap ─────────────────────────────────────────
const showSummaryBanner = computed(() => {
  // Visible dès qu'on est sur model/luggage et qu'on a au moins la marque.
  return Boolean(draft.value.brand_name) && (step.value === 'model' || step.value === 'luggage')
})

const selectedBrandLogo = computed<string | null>(() => {
  const b = brands.value.find((x) => x.id === draft.value.brand_id)
  return normalizeBrandLogo(b?.logo) || null
})

const selectedModel = computed<VehicleModel | null>(() => {
  if (!draft.value.model_id) return null
  return models.value.find((m) => m.id === draft.value.model_id) || null
})

// Le backend renvoie length/height en mètres (ex: 4.27 = 4.27m). On affiche en m.
const modelLengthM = computed<number>(() => Number(selectedModel.value?.length || 0))
const modelHeightM = computed<number>(() => Number(selectedModel.value?.height || 0))
const hasDims = computed(() => modelLengthM.value > 0 || modelHeightM.value > 0)

// Calculs synthèse "max" affichés en bas de step luggage.
const maxRoofCmExtra = computed<number>(() => draft.value.hasLuggage ? draft.value.roofHeight : 0)
const maxTrailerCmExtra = computed<number>(() => draft.value.hasTrailer ? Math.round((draft.value.trailerLength || 0) * 100) : 0)
const maxLengthM = computed<number>(() => modelLengthM.value + (draft.value.hasTrailer ? draft.value.trailerLength : 0))
const maxHeightM = computed<number>(() => modelHeightM.value + (draft.value.hasLuggage ? draft.value.roofHeight / 100 : 0))

function filterByText<T>(items: T[], q: string, getter: (item: T) => string): T[] {
  const needle = normalizeSearchText(q)
  if (!needle) return items
  return items.filter((it) => normalizeSearchText(getter(it)).includes(needle))
}

function formatLengthMeters(raw: number | string | undefined): string {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return ''
  return n.toFixed(2)
}

const PREFETCH_FALLBACK_TOP_N = 10

async function pickType(type: VehicleType): Promise<void> {
  if (SKIPPED_TYPES.has(type)) {
    // Types sans marque/modèle (Aucun, Vélo). On efface TOUT résidu d'un choix
    // précédent (marque/modèle/dims/plaque) — sinon le récap garde p.ex.
    // "Volkswagen Arteon" après un passage à "Aucun".
    clearVehicleDetails()
    // "Aucun" (NoCar) = zéro véhicule → type ET code vides ⇒ véhicule `null`
    // dans tous les payloads (availability + booking) et récap "Aucun véhicule".
    // Le vélo reste un véhicule déclaré (code 'Bike').
    draft.value.type = type === 'NoCar' ? '' : type
    draft.value.code = type === 'NoCar' ? '' : type
    commit()
    emit('close')
    return
  }
  draft.value.type = type
  draft.value.code = ''
  draft.value.model_id = ''
  draft.value.model_name = ''
  step.value = type === 'Moto' ? 'plate' : 'brand'
  if (step.value === 'brand') {
    await loadBrands()
    void prefetchPreferredModels()
  }
}

// Réinitialise les détails véhicule du draft (marque/modèle/dims/plaque) — appelé
// quand on bascule vers un type sans marque (Aucun / Vélo), pour ne pas traîner
// la sélection précédente jusqu'au récap ou au payload.
function clearVehicleDetails(): void {
  draft.value.brand_id = ''
  draft.value.brand_name = ''
  draft.value.model_id = ''
  draft.value.model_name = ''
  draft.value.code = ''
  draft.value.plate = ''
  draft.value.hasLuggage = false
  draft.value.roofHeight = 0
  draft.value.roofLength = 0
  draft.value.hasTrailer = false
  draft.value.trailerLength = 0
}

async function loadBrands(): Promise<void> {
  if (brands.value.length > 0) return
  brandsLoading.value = true
  try { localBrands.value = await api.listVehicleBrands() }
  catch (err) { store.error = { code: 'BRANDS_LOAD_FAILED', message: err instanceof Error ? err.message : 'Failed to load brands' } }
  finally { brandsLoading.value = false }
}

async function prefetchPreferredModels(): Promise<void> {
  const preferred = brands.value.filter((b) => isPreferred(b))
  const targets = preferred.length > 0 ? preferred : brands.value.slice(0, PREFETCH_FALLBACK_TOP_N)
  const toFetch = targets.filter((b) => b.id && !store.modelsByBrand[b.id])
  await Promise.all(toFetch.map(async (b) => {
    try { store.modelsByBrand[b.id] = await api.listVehicleModels(b.id) }
    catch { /* silent */ }
  }))
}

function isPreferred(brand: Brand): boolean {
  const flag = brand.prefer
  if (typeof flag === 'boolean') return flag
  return String(flag ?? '').toLowerCase() === 'yes'
}

async function loadModels(brandId: string): Promise<void> {
  const cached = store.modelsByBrand[brandId]
  if (cached) { models.value = cached; return }
  modelsLoading.value = true
  try {
    const list = await api.listVehicleModels(brandId)
    store.modelsByBrand[brandId] = list
    models.value = list
  }
  catch (err) { store.error = { code: 'MODELS_LOAD_FAILED', message: err instanceof Error ? err.message : 'Failed to load models' } }
  finally { modelsLoading.value = false }
}

// Auto-advance : la sélection marque/modèle déclenche immédiatement le passage
// à l'étape suivante (UX standard de tunnels de booking — on évite le double
// clic "sélectionner puis continuer").
async function pickBrand(b: Brand): Promise<void> {
  draft.value.brand_id = b.id; draft.value.brand_name = b.name
  draft.value.model_id = ''; draft.value.model_name = ''
  // Reset défensif : on vide la liste models AVANT de switch d'étape, sinon
  // l'UI affiche brièvement les modèles de la marque précédente (visible si
  // le fetch traîne ou si le cache n'est pas chaud pour la nouvelle marque).
  models.value = []
  modelQuery.value = ''
  step.value = 'model'
  await loadModels(b.id)
}

function pickModel(m: VehicleModel): void {
  draft.value.model_id = m.id
  draft.value.model_name = m.model
  draft.value.code = m.code || m.id
  step.value = 'luggage'
}

function goNext(): void {
  if (isFinalStep.value) { commit(); emit('close'); return }
  const order = stepOrder()
  const next = order[order.indexOf(step.value) + 1]
  if (!next) return
  step.value = next
  if (next === 'model' && draft.value.brand_id) void loadModels(draft.value.brand_id)
}

function goBack(): void {
  const order = stepOrder()
  const prev = order[order.indexOf(step.value) - 1]
  if (prev) step.value = prev
}

function stepOrder(): Step[] {
  if (draft.value.type === 'Moto') return ['type', 'plate']
  if (SKIPPED_TYPES.has(draft.value.type as VehicleType)) return ['type']
  return ['type', 'brand', 'model', 'luggage']
}

function commit(): void {
  // Dimensions du modèle (mètres) : depuis le catalogue si chargé ; sinon on
  // conserve celles déjà en store quand le modèle n'a pas changé (le catalogue
  // n'est pas rechargé si l'user ne repasse pas par l'étape modèle).
  const prev = store.search.vehicle
  const dims = selectedModel.value
    ?? (prev.vehicle_id && prev.vehicle_id === draft.value.model_id ? prev : null)
  const v: Vehicle = {
    type: draft.value.type || '',
    code: draft.value.code,
    // Les UUID internes vivent dans brand_id / vehicle_id. `brand` et
    // `vehicule_code` portent le NOM de marque et le CODE véhicule (pas l'UUID),
    // cohérent avec le funnel SaaS (sinon l'UI affiche l'UUID au lieu de "Citroën").
    brand_id: draft.value.brand_id || undefined,
    brand: draft.value.brand_name || undefined,
    brand_name: draft.value.brand_name || undefined,
    vehicle_id: draft.value.model_id || undefined,
    // Lien explicite vers la fiche catalogue (VehicleData) → repris dans le
    // dossier beintravel (`__vehicle.vehicle_data_id`).
    vehicle_data_id: draft.value.model_id || undefined,
    vehicule_code: draft.value.code || undefined,
    model: draft.value.model_name || undefined,
    // Logo de marque (issu du catalogue backend `brand.logo`). Conservé si le
    // modèle n'a pas changé et que le catalogue n'a pas été rechargé.
    logo: selectedBrandLogo.value || (prev.brand_id === draft.value.brand_id ? prev.logo : undefined) || undefined,
    length: Number(dims?.length) || undefined,
    height: Number(dims?.height) || undefined,
    identification: draft.value.plate || null,
  }
  if (draft.value.hasLuggage && draft.value.roofHeight > 0) {
    // length conservé en cm (legacy `roofLength` ; 0 si non spécifié).
    v.roof = { height: draft.value.roofHeight, length: draft.value.roofLength || 0 }
  }
  if (draft.value.hasTrailer && draft.value.trailerLength > 0) {
    // Convention backend : trailer.length en mètres, height à 0 si non utilisé.
    v.trailer = { height: 0, length: draft.value.trailerLength }
  }
  store.search.vehicle = v
}

function buildDraftFromStore(): VehicleDraft {
  const v = store.search.vehicle
  const trailerLen = Number(v.trailer?.length || 0)
  return {
    type: (v.type as VehicleType) || '',
    brand_id: v.brand_id ?? '',
    brand_name: v.brand_name ?? '',
    model_id: v.vehicle_id ?? '',
    model_name: v.model ?? '',
    code: v.code ?? '',
    plate: typeof v.identification === 'string' ? v.identification : '',
    hasLuggage: !!v.roof,
    roofHeight: v.roof ? Number(v.roof.height) || 0 : 0,
    roofLength: v.roof ? Number(v.roof.length) || 0 : 0,
    hasTrailer: trailerLen > 0,
    trailerKind: 'caravan',
    trailerLength: trailerLen,
  }
}
</script>

<!-- Styles dans /styles.css (Web Component Shadow DOM → scoped ne passe pas). -->
